import React, { useState } from 'react';
import { 
  Card, 
  Form, 
  Select, 
  InputNumber, 
  Input, 
  Button, 
  DatePicker,
  Space,
  Typography,
  List,
  Tag,
  Progress,
  message,
  Row,
  Col,
  Empty,
  Modal,
  Image
} from 'antd';
import { Upload } from 'antd';
import { 
  EditOutlined, 
  SaveOutlined, 
  ClockCircleOutlined,
  CopyOutlined
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAuthStore } from '../../stores/authStore';

const { TextArea } = Input;
const { Option } = Select;
const { Text } = Typography;

/**
 * DailyLog 工作日志页面组件
 * 功能：用于填写个人工作日志（含任务关联、进度、耗时、备注等）并在右侧展示最近日志列表。
 * 说明：
 * 1) 已将 antd Card 组件的 bodyStyle 替换为 v5 推荐的 styles.body，避免控制台弃用警告。
 * 2) 经理/管理员账号在“我的工作日志”区块将展示全体成员的日志，普通成员仅展示自己的日志。
 */
const DailyLog = () => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [copyingPlan, setCopyingPlan] = useState(false);
  const [uploadedImageUrls, setUploadedImageUrls] = useState([]);
  const [uploadFileList, setUploadFileList] = useState([]);
  // 新增：调休对话框与状态
  const [leaveModalVisible, setLeaveModalVisible] = useState(false);
  const [leaveStartDate, setLeaveStartDate] = useState(dayjs().add(1, 'day'));
  const [leaveEndDate, setLeaveEndDate] = useState(dayjs().add(1, 'day'));
  const [leaveSubmitting, setLeaveSubmitting] = useState(false);
  const [leaveApplied, setLeaveApplied] = useState(false);
  
  // 获取认证状态
  const { user, token, isAuthenticated } = useAuthStore();
  // 去除管理视角：统一仅显示当前登录用户的日志

  // 获取我的任务（用于选择）
  const { data: myTasksData, isLoading: tasksLoading, error: tasksError } = useQuery(
    'myTasksForLog',
    () => axios.get('/tasks/my', {
      params: { status: 'pending,in_progress' }
    }).then(res => res.data.data),
    { 
      staleTime: 5 * 60 * 1000,
      onError: (error) => {
         console.error('获取任务数据失败:', error);
         message.error('获取任务列表失败，请刷新页面重试');
       }
    }
  );

  // 获取工作日志：固定仅查询当前用户
  const { data: allLogsData, isLoading: logsLoading } = useQuery(
    ['userAllLogs', user?.id],
    () => axios.get('/daily-logs', {
      params: {
        limit: 50,
        userId: user?.id
      }
    }).then(res => res.data.data),
    { refetchInterval: 30000, enabled: !!user?.id }
  );

  // 创建日志
  const createLogMutation = useMutation(
    (logData) => axios.post('/daily-logs', logData),
    {
      onSuccess: () => {
        message.success('日志提交成功');
        form.resetFields();
        queryClient.invalidateQueries('userAllLogs');
        queryClient.invalidateQueries('myTasks');
      },
      onError: (error) => {
        message.error(error.response?.data?.message || '提交失败');
      }
    }
  );

  /**
   * 复制昨日计划功能
   * 获取当前用户昨天在当前选择任务所属项目下的明日工作计划
   */
  const handleCopyYesterdayPlan = async () => {
    const currentTaskId = form.getFieldValue('taskId');
    if (!currentTaskId) {
      message.warning('请先选择关联任务');
      return;
    }

    setCopyingPlan(true);
    try {
      // 获取当前任务信息以确定项目ID
      const taskResponse = await axios.get(`/tasks/${currentTaskId}`);
      const currentTask = taskResponse.data.data.task;
      const projectId = currentTask.projectId;

      // 计算昨天的日期
      const yesterday = selectedDate.subtract(1, 'day').format('YYYY-MM-DD');

      // 获取昨天该项目下当前用户的日志
      const logsResponse = await axios.get('/daily-logs', {
        params: {
          userId: user.id,
          startDate: yesterday,
          endDate: yesterday,
          projectId: projectId,
          limit: 50
        }
      });

      const yesterdayLogs = logsResponse.data.data.logs || [];
      
      if (yesterdayLogs.length === 0) {
        message.info('昨天没有找到该项目的工作日志');
        return;
      }

      // 查找有明日计划的日志，优先选择最新的
      const logWithPlan = yesterdayLogs
        .filter(log => log.tomorrowPlan && log.tomorrowPlan.trim())
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

      if (!logWithPlan) {
        message.info('昨天的日志中没有找到明日工作计划');
        return;
      }

      // 将昨日的明日计划填充到当前表单的明日计划字段
      form.setFieldsValue({
        tomorrowPlan: logWithPlan.tomorrowPlan
      });

      message.success('已复制昨日计划内容');
    } catch (error) {
      console.error('复制昨日计划失败:', error);
      message.error('复制昨日计划失败，请重试');
    } finally {
      setCopyingPlan(false);
    }
  };

  /**
   * 复制昨日计划到今日完成内容
   * 中文函数级注释：从昨天日志中找到“明日工作计划（tomorrowPlan）”，填充到当前表单的“今日完成内容（todayWork）”字段
   */
  const handleCopyYesterdayPlanToWork = async () => {
    const currentTaskId = form.getFieldValue('taskId');
    if (!currentTaskId) {
      message.warning('请先选择关联任务');
      return;
    }

    setCopyingPlan(true);
    try {
      // 获取当前任务信息以确定项目ID
      const taskResponse = await axios.get(`/tasks/${currentTaskId}`);
      const currentTask = taskResponse.data.data.task;
      const projectId = currentTask.projectId;

      // 计算昨天的日期
      const yesterday = selectedDate.subtract(1, 'day').format('YYYY-MM-DD');

      // 获取昨天该项目下当前用户的日志
      const logsResponse = await axios.get('/daily-logs', {
        params: {
          userId: user.id,
          startDate: yesterday,
          endDate: yesterday,
          projectId: projectId,
          limit: 50
        }
      });

      const yesterdayLogs = logsResponse.data.data.logs || [];
      
      if (yesterdayLogs.length === 0) {
        message.info('昨天没有找到该项目的工作日志');
        return;
      }

      // 查找有明日计划的日志，优先选择最新的
      const logWithPlan = yesterdayLogs
        .filter(log => log.tomorrowPlan && log.tomorrowPlan.trim())
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

      if (!logWithPlan) {
        message.info('昨天的日志中没有找到明日工作计划');
        return;
      }

      // 将昨日的明日计划填充到当前表单的今日完成内容字段
      form.setFieldsValue({
        todayWork: logWithPlan.tomorrowPlan
      });

      message.success('已复制昨日计划到今日完成内容');
    } catch (error) {
      console.error('复制昨日计划到今日完成内容失败:', error);
      message.error('复制昨日计划到今日完成内容失败，请重试');
    } finally {
      setCopyingPlan(false);
    }
  };

  /**
   * 复制昨日完成内容功能
   * 获取当前用户昨天在当前选择任务所属项目下的今日完成内容
   */
  const handleCopyYesterdayWork = async () => {
    const currentTaskId = form.getFieldValue('taskId');
    if (!currentTaskId) {
      message.warning('请先选择关联任务');
      return;
    }

    setCopyingPlan(true);
    try {
      // 获取当前任务信息以确定项目ID
      const taskResponse = await axios.get(`/tasks/${currentTaskId}`);
      const currentTask = taskResponse.data.data.task;
      const projectId = currentTask.projectId;

      // 计算昨天的日期
      const yesterday = selectedDate.subtract(1, 'day').format('YYYY-MM-DD');

      // 获取昨天该项目下当前用户的日志
      const logsResponse = await axios.get('/daily-logs', {
        params: {
          userId: user.id,
          startDate: yesterday,
          endDate: yesterday,
          projectId: projectId,
          limit: 50
        }
      });

      const yesterdayLogs = logsResponse.data.data.logs || [];
      
      if (yesterdayLogs.length === 0) {
        message.info('昨天没有找到该项目的工作日志');
        return;
      }

      // 查找有今日完成内容的日志，优先选择最新的
      const logWithWork = yesterdayLogs
        .filter(log => log.todayWork && log.todayWork.trim())
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];

      if (!logWithWork) {
        message.info('昨天的日志中没有找到完成内容');
        return;
      }

      // 将昨日的完成内容填充到当前表单的今日完成内容字段
      form.setFieldsValue({
        todayWork: logWithWork.todayWork
      });

      message.success('已复制昨日完成内容');
    } catch (error) {
      console.error('复制昨日完成内容失败:', error);
      message.error('复制昨日完成内容失败，请重试');
    } finally {
      setCopyingPlan(false);
    }
  };

  const handleSubmit = (values) => {
    const hasTask = !!values.taskId;
    const logData = {
      ...values,
      ...(hasTask ? {} : { taskId: undefined, progressAfter: values.progressAfter ?? 0 }),
      logDate: selectedDate.format('YYYY-MM-DD'),
      imageUrls: uploadedImageUrls,
      imageUrl: uploadedImageUrls[0] || undefined
    };
    createLogMutation.mutate(logData);
  };

  const handleTaskChange = async (taskId) => {
    if (!taskId) return;
    
    try {
      // 获取任务详情以获取当前进度
      const response = await axios.get(`/tasks/${taskId}`);
      const task = response.data.data.task;
      
      // 将当前进度值加上5%填入更新后进度，但不超过100%
      const newProgress = Math.min(task.progress + 5, 100);
      
      form.setFieldsValue({
        progressAfter: newProgress
      });
    } catch (error) {
      console.error('获取任务详情失败:', error);
    }
  };

  /**
   * 处理进度输入框的鼠标滚轮事件
   * 中文注释：鼠标滚轮向上滚动增加1%，向下滚动减少1%，范围限制在0-100%
   */
  const handleProgressWheel = (e) => {
    e.preventDefault();
    const currentValue = form.getFieldValue('progressAfter') || 0;
    const delta = e.deltaY > 0 ? -1 : 1; // 向下滚动减少，向上滚动增加
    const newValue = Math.max(0, Math.min(100, currentValue + delta));
    form.setFieldsValue({ progressAfter: newValue });
  };

  /**
   * 处理工作时长输入框的鼠标滚轮事件
   * 中文注释：鼠标滚轮向上滚动增加1小时，向下滚动减少1小时，范围限制在0-24小时
   */
  const handleWorkHoursWheel = (e) => {
    e.preventDefault();
    const currentValue = form.getFieldValue('workHours') || 0;
    const delta = e.deltaY > 0 ? -1 : 1; // 向下滚动减少，向上滚动增加
    const newValue = Math.max(0, Math.min(24, currentValue + delta));
    form.setFieldsValue({ workHours: newValue });
  };

  /**
   * 生成日期区间（含端点）工具函数，跳过周末
   * 中文注释：给定开始与结束日期（dayjs 对象），返回包含所有工作日日期字符串（YYYY-MM-DD）的数组，自动跳过周六周日
   */
  const generateDateRange = (start, end) => {
    const dates = [];
    let cursor = start.startOf('day');
    const last = end.startOf('day');
    while (cursor.isSame(last) || cursor.isBefore(last)) {
      // 跳过周末：0=周日，6=周六
      const dayOfWeek = cursor.day();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        dates.push(cursor.format('YYYY-MM-DD'));
      }
      cursor = cursor.add(1, 'day');
    }
    return dates;
  };

  /**
   * 打开调休申请对话框
   * 中文注释：无需选择“关联任务”也可申请调休；打开弹窗并将开始/截止日期默认设为明天
   */
  const handleOpenLeaveModal = () => {
    setLeaveStartDate(dayjs().add(1, 'day'));
    setLeaveEndDate(dayjs().add(1, 'day'));
    setLeaveModalVisible(true);
  };

  /**
   * 提交调休申请并自动创建调休日志
   * 中文注释：为选择的日期区间逐日创建日志（todayWork="调休（系统自动登记）"，workHours=0）；支持“无关联任务”场景：未选择任务时不携带 taskId，progressAfter 记为 0
   */
  const handleSubmitLeave = async () => {
    const currentTaskId = form.getFieldValue('taskId');

    const start = leaveStartDate; // 使用用户选择的开始日期
    const end = leaveEndDate ? leaveEndDate : start;
    if (end.isBefore(start, 'day')) {
      message.error('截止日期不能早于开始日期');
      return;
    }

    setLeaveSubmitting(true);
    try {
      // 获取进度：若选择了任务，优先取表单中的 progressAfter，否则拉取任务详情；未选择任务时固定为 0
      let progressAfter;
      if (currentTaskId) {
        progressAfter = form.getFieldValue('progressAfter');
        if (progressAfter === undefined || progressAfter === null) {
          const resp = await axios.get(`/tasks/${currentTaskId}`);
          progressAfter = resp.data?.data?.task?.progress ?? 0;
        }
      } else {
        progressAfter = 0;
      }

      const dates = generateDateRange(start, end);
      let success = 0;
      let skipped = 0;

      for (const d of dates) {
        try {
          const payload = {
            logDate: d,
            todayWork: '调休（系统自动登记）',
            tomorrowPlan: '',
            workHours: 0
          };
          if (currentTaskId) {
            payload.taskId = currentTaskId;
            payload.progressAfter = progressAfter;
          } else {
            // 无关联任务，后端支持不传 taskId，progressAfter 默认为 0
            payload.progressAfter = 0;
          }

          await axios.post('/daily-logs', payload);
          success += 1;
        } catch (e) {
          skipped += 1;
          console.warn('创建调休日志失败/跳过：', d, e?.response?.data || e?.message);
        }
      }

      if (success > 0) {
        message.success(`调休申请成功：已创建 ${success} 条调休日志${skipped > 0 ? `，跳过 ${skipped} 条` : ''}`);
      } else {
        message.info('未创建任何调休日志，可能这些日期已存在日志记录');
      }

      setLeaveApplied(true);
      setLeaveModalVisible(false);
      queryClient.invalidateQueries(['userAllLogs', user?.id]);
    } catch (err) {
      console.error('提交调休失败：', err);
      message.error('提交调休失败，请重试');
    } finally {
      setLeaveSubmitting(false);
    }
  };

  // 展平任务数据用于选择
  const allTasks = myTasksData?.tasksByProject?.reduce((acc, group) => {
    return [...acc, ...group.tasks.map(task => ({
      ...task,
      projectName: group.project.name
    }))];
  }, []) || [];

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'red',
      medium: 'orange',
      low: 'green'
    };
    return colors[priority] || 'default';
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'default',
      in_progress: 'processing',
      completed: 'success'
    };
    return colors[status] || 'default';
  };

  return (
    <div style={{ padding: '24px' }}>
      <Row gutter={[24, 24]}>
        {/* 左侧：日志填写 */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <EditOutlined />
                工作日志填写
              </Space>
            }
            className="status-card"
            style={{ height: '800px' }}
            styles={{
              body: {
                height: 'calc(100% - 57px)',
                overflowY: 'auto',
                padding: '24px'
              }
            }}
          >
            {tasksError && (
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 6 }}>
                <Text type="danger">
                  ⚠️ 获取任务列表失败，请检查网络连接或刷新页面重试
                </Text>
                <div style={{ marginTop: 8 }}>
                  <Button 
                    size="small" 
                    onClick={() => {
                      queryClient.invalidateQueries('myTasksForLog');
                      window.location.reload();
                    }}
                  >
                    刷新页面
                  </Button>
                </div>
              </div>
            )}
            
            {!tasksLoading && !tasksError && allTasks.length === 0 && (
              <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 6 }}>
                <Text type="warning">
                  📝 当前没有可用的任务。可能的原因：
                </Text>
                <ul style={{ marginTop: 8, marginBottom: 0, paddingLeft: 20 }}>
                  <li>您没有被分配任何进行中的任务</li>
                  <li>所有任务都已完成</li>
                  <li>需要项目经理为您分配新任务</li>
                </ul>
              </div>
            )}
            
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{
                logDate: selectedDate,
                workHours: 8
              }}
            >
              <Form.Item
                name="logDate"
                label="日志日期"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  format="YYYY-MM-DD"
                  value={selectedDate}
                  onChange={setSelectedDate}
                />
              </Form.Item>

              <Form.Item
                name="taskId"
                label="关联任务"
                rules={[{ required: allTasks.length > 0, message: '请选择关联任务' }]}
              >
                <Select
                  placeholder={tasksLoading ? "加载任务列表中..." : allTasks.length === 0 ? "暂无可用任务" : "选择要填报的任务"}
                  showSearch
                  optionFilterProp="children"
                  onChange={handleTaskChange}
                  loading={tasksLoading}
                  disabled={tasksLoading || allTasks.length === 0}
                  notFoundContent={tasksLoading ? "加载中..." : "暂无任务"}
                >
                  {allTasks.map(task => (
                    <Option key={task.id} value={task.id}>
                      <div>
                        <Text strong>{task.title}</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {task.projectName} | 当前进度: {task.progress}%
                        </Text>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="progressAfter"
                label="更新后进度 (%)"
                rules={[{ required: allTasks.length > 0, message: '请输入完成进度' }]}
              >
                <InputNumber
                  min={0}
                  max={100}
                  style={{ width: '100%' }}
                  formatter={value => `${value}%`}
                  parser={value => value.replace('%', '')}
                  onWheel={handleProgressWheel}
                />
              </Form.Item>

              <Form.Item
                name="todayWork"
                label={
                  <Space>
                    <span>今日完成内容</span>
                    <Button
                      type="link"
                      size="small"
                      icon={<CopyOutlined />}
                      loading={copyingPlan}
                      onClick={handleCopyYesterdayPlanToWork}
                      style={{ padding: 0, height: 'auto' }}
                    >
                      复制昨日计划
                    </Button>
                  </Space>
                }
                rules={[
                  { required: true, message: '请输入今日完成内容' },
                  { min: 5, message: '内容至少5个字符' }
                ]}
              >
                <TextArea
                  rows={4}
                  placeholder="请详细描述今日完成的工作内容..."
                />
              </Form.Item>

            <Form.Item
              label="上传图片（可多选，空间上传轴测图或效果图、平面和策划上传当日主要工作成果）"
            >
              <Upload
                name="image"
                action="/api/daily-logs/upload-image"
                headers={{ Authorization: `Bearer ${token}` }}
                accept="image/*"
                multiple
                maxCount={9}
                fileList={uploadFileList}
                onChange={(info) => {
                  const { file, fileList } = info;
                  setUploadFileList(fileList);
                  if (file.status === 'error') {
                    const reason = file.response?.message || file.error?.message;
                    message.error(`${file.name} 上传失败${reason ? `：${reason}` : ''}`);
                  }
                  // 收集已上传成功的文件URL（受控模式，确保多选时全部保留）
                  const urls = fileList
                    .filter(f => f.status === 'done' && f.response?.data?.url)
                    .map(f => f.response.data.url);
                  setUploadedImageUrls(urls);
                  if (file.status === 'done') {
                    message.success(`${file.name} 上传成功`);
                  }
                }}
                listType="picture"
              >
                <Button type="default">选择图片上传</Button>
              </Upload>
              {uploadedImageUrls && uploadedImageUrls.length > 0 && (
                <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <Image.PreviewGroup>
                    {uploadedImageUrls.map((u, idx) => (
                      <Image
                        key={idx}
                        src={u}
                        alt={`log-${idx}`}
                        width={160}
                        height={120}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                      />
                    ))}
                  </Image.PreviewGroup>
                </div>
              )}
            </Form.Item>

            <Form.Item
              name="tomorrowPlan"
              label={
                <Space>
                  <span>明日工作计划</span>
                  <Button
                      type="link"
                      size="small"
                      icon={<CopyOutlined />}
                      loading={copyingPlan}
                      onClick={handleCopyYesterdayPlan}
                      style={{ padding: 0, height: 'auto' }}
                    >
                      复制昨日计划
                    </Button>
                  </Space>
                }
                rules={[
                  { required: true, message: '请输入明日计划' },
                  { min: 5, message: '内容至少5个字符' }
                ]}
              >
                <TextArea
                  rows={3}
                  placeholder="请输入明日的工作计划..."
                  disabled={leaveApplied}
                />
              </Form.Item>

              <Form.Item
                name="workHours"
                label="工作时长（小时）"
                rules={[{ required: true, message: '请输入工作时长' }]}
              >
                <InputNumber
                  min={0}
                  max={24}
                  step={0.5}
                  style={{ width: '100%' }}
                  placeholder="请输入今日工作时长"
                  onWheel={handleWorkHoursWheel}
                />
              </Form.Item>

              <Form.Item
                name="issues"
                label="遇到的问题"
              >
                <TextArea
                  rows={2}
                  placeholder="记录遇到的问题或困难（可选）"
                />
              </Form.Item>

              <Form.Item
                name="remarks"
                label="备注"
              >
                <TextArea
                  rows={2}
                  placeholder="其他备注信息（可选）"
                />
              </Form.Item>

              <Form.Item>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={createLogMutation.isLoading}
                  block
                >
                  提交日志
                </Button>
              </Form.Item>
            </Form>
          </Card>
        </Col>

        {/* 右侧：我的/团队工作日志列表 */}
        <Col xs={24} lg={12}>
          <Card 
            title={
              <Space>
                <ClockCircleOutlined />
                我的工作日志
              </Space>
            }
            className="status-card"
            style={{ height: '800px' }}
            styles={{
              body: {
                height: 'calc(100% - 57px)',
                overflow: 'hidden',
                padding: 0
              }
            }}
          >
            <div style={{ 
              height: '100%', 
              overflowY: 'auto',
              padding: '24px'
            }}>
              {!allLogsData?.logs || allLogsData.logs.length === 0 ? (
                <Empty 
                  description="暂无工作记录"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              ) : (
                <List
                  loading={logsLoading}
                  dataSource={allLogsData?.logs || []}
                  renderItem={(log) => (
                    <List.Item style={{ padding: '16px 0' }}>
                      <List.Item.Meta
                        title={
                          <Space direction="vertical" size={2}>
                            {/* 项目名称 */}
                            {log.task?.project?.name && (
                              <Text type="secondary" style={{ fontSize: '12px', color: '#1890ff' }}>
                                [{log.task.project.name}]
                              </Text>
                            )}
                            {/* 任务名称和标签 */}
                            <Space>
                              <Text strong>{log.task?.title || '未知任务'}</Text>
                              {/* 移除管理视角下的“填报人”显示，保持仅显示本人日志 */}
                              <Tag color="blue">
                                {log.workHours}小时
                              </Tag>
                              <Tag color="green">
                                {dayjs(log.logDate).format('MM-DD')}
                              </Tag>
                            </Space>
                          </Space>
                        }
                        description={
                          <Space direction="vertical" size="small" style={{ width: '100%' }}>
                            <Text>
                              <strong>完成内容:</strong> {log.todayWork}
                            </Text>
                            {(() => {
                              const imgs = (() => {
                                if (Array.isArray(log.imageUrls)) return log.imageUrls;
                                if (typeof log.imageUrls === 'string' && log.imageUrls.trim()) {
                                  try {
                                    const arr = JSON.parse(log.imageUrls);
                                    if (Array.isArray(arr)) return arr;
                                  } catch (e) {}
                                }
                                return log.imageUrl ? [log.imageUrl] : [];
                              })();
                              return imgs.length > 0 ? (
                                <div style={{ margin: '8px 0' }}>
                                  <Image.PreviewGroup>
                                    {imgs.map((u, idx) => (
                                      <Image key={idx} src={u} alt={`日志图片-${idx}`} style={{ maxWidth: '100%', maxHeight: 320, borderRadius: 4, marginRight: 8 }} />
                                    ))}
                                  </Image.PreviewGroup>
                                </div>
                              ) : null;
                            })()}
                            
                            {log.tomorrowPlan && (
                              <Text>
                                <strong>明日计划:</strong> {log.tomorrowPlan}
                              </Text>
                            )}
                            
                            {log.issues && (
                              <Text type="warning">
                                <strong>问题:</strong> {log.issues}
                              </Text>
                            )}

                            <div style={{ marginTop: '8px' }}>
                              <Text type="secondary">
                                进度: {log.progressBefore}% → {log.progressAfter}%
                              </Text>
                              <Progress 
                                percent={log.progressAfter} 
                                size="small"
                                style={{ marginTop: '4px' }}
                                status={log.progressAfter === 100 ? 'success' : 'active'}
                              />
                            </div>
                          </Space>
                        }
                      />
                    </List.Item>
                  )}
                />
              )}
            </div>
          </Card>
        </Col>

        {/* 新增：调休申请弹窗 */}
        <Modal
          title="申请调休"
          open={leaveModalVisible}
          onOk={handleSubmitLeave}
          onCancel={() => setLeaveModalVisible(false)}
          confirmLoading={leaveSubmitting}
          okText="确认申请"
          cancelText="取消"
        >
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <div>
              <Text type="secondary" style={{ marginRight: 8 }}>开始日期</Text>
              <DatePicker
                style={{ width: '100%' }}
                value={leaveStartDate}
                onChange={(v) => {
                  const newStartDate = v || dayjs().add(1, 'day');
                  setLeaveStartDate(newStartDate);
                  // 如果截止日期早于新的开始日期，则调整截止日期
                  if (leaveEndDate.isBefore(newStartDate, 'day')) {
                    setLeaveEndDate(newStartDate);
                  }
                }}
                allowClear={false}
                disabledDate={(current) => current && current.isBefore(dayjs().add(1, 'day'), 'day')}
                format="YYYY-MM-DD"
              />
              <div style={{ marginTop: 6 }}>
                <Text type="secondary">不能选择今天及之前的日期</Text>
              </div>
            </div>
            <div>
              <Text type="secondary" style={{ marginRight: 8 }}>截止日期</Text>
              <DatePicker
                style={{ width: '100%' }}
                value={leaveEndDate}
                onChange={(v) => setLeaveEndDate(v || leaveStartDate)}
                allowClear={false}
                disabledDate={(current) => current && current.isBefore(leaveStartDate, 'day')}
                format="YYYY-MM-DD"
              />
            </div>
            <div style={{ background: '#fffbe6', border: '1px solid #ffe58f', padding: 8, borderRadius: 6 }}>
              <Text type="secondary">说明：提交后系统会为选择的日期逐日新增“调休”日志（工作时长 0），并使“明日工作计划”不可编辑。</Text>
            </div>
          </Space>
        </Modal>
      </Row>
    </div>
  );
};

export default DailyLog;
